'use client';
import React from 'react';
import ProductCard from '@/components/ui/ProductCard';
import { ProductBase } from '@/types/product';
import { BreadcrumbMenu } from '@/types/menu';
import { generateColor } from '@/utils/productMapper';

interface ProductsGridProps {
  breadcrumbMenu: BreadcrumbMenu;
  className?: string;
  products: ProductBase[];
  viewMode: 'grid' | 'list';
  onToggleFavorite: (productId: string) => void;
}

const ProductsGrid: React.FC<ProductsGridProps> = ({
  breadcrumbMenu,
  className = '',
  products,
  viewMode,
  onToggleFavorite
}) => {
  // Verificar si hay productos
  if (!products || products.length === 0) {
    return (
      <div data-component-id="src/components/sections/ProductsGrid.tsx:26:6" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="26" data-component-file="ProductsGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex flex-col items-center justify-center py-12 ${className}`}>
        <div data-component-id="src/components/sections/ProductsGrid.tsx:27:8" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="27" data-component-file="ProductsGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
          <div data-component-id="src/components/sections/ProductsGrid.tsx:28:10" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="28" data-component-file="ProductsGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-16%20h-16%20mx-auto%20mb-4%20rounded-full%20flex%20items-center%20justify-center%22%7D"
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(61, 28, 26, 0.25)' }}
          >
            <svg data-component-id="src/components/sections/ProductsGrid.tsx:32:12" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="32" data-component-file="ProductsGrid.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-8%20h-8%22%7D"
              className="w-8 h-8"
              fill="none"
              stroke="#3D1C1A"
              viewBox="0 0 24 24"
            >
              <path data-component-id="src/components/sections/ProductsGrid.tsx:38:14" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="38" data-component-file="ProductsGrid.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
          </div>
          <h3 data-component-id="src/components/sections/ProductsGrid.tsx:46:10" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="46" data-component-file="ProductsGrid.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20mb-2%22%2C%22textContent%22%3A%22No%20se%20encontraron%20productos%20disponibles%20para%20esta%20categor%C3%ADa%22%7D"
            className="text-lg mb-2"
            style={{
              fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
              fontWeight: '700',
              color: '#3D1C1A'
            }}
          >
            No se encontraron productos disponibles para esta categoría
          </h3>

          <div data-component-id="src/components/sections/ProductsGrid.tsx:57:10" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="57" data-component-file="ProductsGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%22%2C%22textContent%22%3A%22Intenta%20con%20otra%20categor%C3%ADa%20o%20vuelve%20m%C3%A1s%20tarde.%22%7D"
            className="text-sm"
            style={{
              fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
              fontWeight: '400',
              color: '#3D1C1A'
            }}
          >
            Intenta con otra categoría o vuelve más tarde.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/components/sections/ProductsGrid.tsx:73:4" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="73" data-component-file="ProductsGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${className}`}>
      {viewMode === 'grid' ? (
        <div data-component-id="src/components/sections/ProductsGrid.tsx:75:8" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="75" data-component-file="ProductsGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20lg%3Agrid-cols-3%20xl%3Agrid-cols-4%20gap-6%20mb-8%22%7D" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {products.map((product, index) => (
            <div data-component-id="src/components/sections/ProductsGrid.tsx:77:12" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="77" data-component-file="ProductsGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%22%7D" key={product.id} className="flex justify-center">
              <ProductCard data-component-id="src/components/sections/ProductsGrid.tsx:78:14" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="78" data-component-file="ProductsGrid.tsx" data-component-name="ProductCard" data-component-content="%7B%22elementName%22%3A%22ProductCard%22%2C%22className%22%3A%22%22%7D"
                index={index}
                breadcrumbMenu={breadcrumbMenu}
                product={product}
                backgroundColor={generateColor(index) || '#FFFFFF'}
                onFavoriteToggle={onToggleFavorite}
                viewMode={viewMode}
                className=""
              />
            </div>
          ))}
        </div>
      ) : (
        <div data-component-id="src/components/sections/ProductsGrid.tsx:91:8" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="91" data-component-file="ProductsGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20gap-6%20mb-8%22%7D" className="flex flex-col gap-6 mb-8">
          {products.map((product, index) => (
            <div data-component-id="src/components/sections/ProductsGrid.tsx:93:12" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="93" data-component-file="ProductsGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%22%7D" key={product.id} className="w-full">
              <ProductCard data-component-id="src/components/sections/ProductsGrid.tsx:94:14" data-component-path="src/components/sections/ProductsGrid.tsx" data-component-line="94" data-component-file="ProductsGrid.tsx" data-component-name="ProductCard" data-component-content="%7B%22elementName%22%3A%22ProductCard%22%2C%22className%22%3A%22w-full%22%7D"
                index={index}
                breadcrumbMenu={breadcrumbMenu}
                product={product}
                backgroundColor={generateColor(index) || '#FFFFFF'}
                onFavoriteToggle={onToggleFavorite}
                viewMode={viewMode}
                className="w-full"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductsGrid; 